import { CenterContainer } from 'components/common/CenterContainer'
import Image from 'next/image'
import React from 'react'
import Footer from 'components/Footer'
import language from 'services/Language'
import { NextSeo } from 'next-seo'
import EffectiveNav from 'components/Nav/EffectiveNav'
import {
  Typography,
  Button,
  MarketetingContentSection,
  MarketingContentSectionProps
} from 'components/common'
import MFDAnnouncementBanner from 'components/Collectives/Common/MFDAnnouncementBanner'

const analyticsPreFix = 'Collectives'

const metaImage =
  'https://i.upstreamapp.com/ogImages/collectives/Open+Graph+Image+3.png'

const metaUrl = 'https://upstreamapp.com/collectives'

const pageWidth = 'max-w-[1440px]'

const Card: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return <div className="bg-white rounded-lg p-4">{children}</div>
}

const LearnMoreAboutDaosData: Array<MarketingContentSectionProps> = [
  {
    description: 'What is a DAO and how does it work?',
    image: '/static/collective/topDAO.png',
    imgAlt: 'Upstream Collectice DAO',
    trackingString: `${analyticsPreFix}: DAOs: What is a DAO and how does it work`,
    link: 'https://youtu.be/ar_UlNIqfQw'
  },
  {
    description: 'How Do I Join a DAO?',
    image: '/static/collective/middleDAO.png',
    imgAlt: 'How Do I Join a DAO?',
    trackingString: `${analyticsPreFix}: DAOs: How Do I Join a DAO`,
    link: 'https://guide.upstreamapp.com/dao-members/joining-a-dao'
  }
]

const LearnMoreAboutNftsData: Array<MarketingContentSectionProps> = [
  {
    description: '8 Steps to Buying Your First NFT',
    image: '/static/collective/topNFT.png',
    imgAlt: '8 Steps to Buying Your First NFT',
    trackingString: `${analyticsPreFix}: NFTs: 8 Steps to Buying Your First NFT`,
    link: 'https://blog.upstreamapp.com/8-steps-to-buying-your-first-nft/'
  },
  {
    description: 'How to Turn a NFT Project into a Company',
    image: '/static/collective/middleNFT.png',
    imgAlt: 'How to Turn a NFT Project into a Company',
    trackingString: `${analyticsPreFix}: NFTs: How to Turn a NFT Project into a Company`,
    link: 'https://www.youtube.com/watch?v=GMzUNJWCKPE'
  },
  {
    description: 'How to Set Up Your NFT & Crypto Wallet',
    image: '/static/collective/bottomNFT.png',
    imgAlt: 'How to Set Up Your NFT & Crypto Wallet',
    trackingString: `${analyticsPreFix}: NFTs: How to Set Up Your NFT & Crypto Wallet`,
    link: 'https://www.youtube.com/watch?v=dZvlrGfHmCs'
  }
]

function CollectivesPage() {
  return (
    <>
      <NextSeo
        openGraph={{
          url: metaUrl,
          images: [
            {
              url: metaImage,
              width: 1200,
              height: 630
            }
          ]
        }}
        twitter={{
          cardType: 'summary_large_image'
        }}
        additionalMetaTags={[
          {
            name: 'twitter:image',
            content: metaImage
          },
          {
            name: 'twitter:image:alt',
            content: 'DAOs Introduction Flyer'
          }
        ]}
      />

      <MFDAnnouncementBanner bannerStyle="grey" />

      <EffectiveNav />

      <div className="w-full -mt-24">
        <div className="gradient-box-top h-[850px] sm:h-[630px] flex items-center flex-col gap-2.5 px-6  min-sm:pt-32 pt-36 pb-64 min-sm:pb-64">
          <div>
            <Typography
              size="lg"
              className="uppercase tracking-[0.5px] text-gray-600 leading-[28px] text-center"
              fontWeight="light"
            >
              {language.collectiveMarketing.poweredByBlockchain}
            </Typography>
          </div>

          <div>
            <h6 className="special-collective-text text-[64px] tracking-[-1px] leading-[74px] font-bold text-center">
              {language.collectiveMarketing.intro}
            </h6>
          </div>
          <div className="pb-8">
            <Typography
              className="text-[26px] text-gray-800 tracking-[-0.5px] leading-[34px] text-center"
              fontWeight="light"
            >
              {language.collectiveMarketing.introSub}
            </Typography>
          </div>

          <div className="flex items-center flex-col text-center gap-14">
            <Image
              src="/static/collective/hero.svg"
              width={210}
              height={153}
              alt="Upstream DAO Hero"
            />
          </div>
        </div>

        <div className="gradient-box flex items-center justify-center p-8 sm:p-12 min-h-0">
          <div className={`${pageWidth} py-8`}>
            {/* SHOWS UP ON LARGE & UP */}
            <Typography
              fontWeight="bold"
              className="  text-4xl md:text-3xl xl:text-5xl	text-white"
            >
              {language.collectiveMarketing.communitiesWhy}
            </Typography>
          </div>
        </div>

        <CenterContainer>
          <div className="mt-28">
            <div className="flex flex-col sm:flex-row">
              <div className="flex-1">
                <div className="flex justify-center sm:justify-start mb-0 sm:mb-8 mx-4">
                  <Image
                    src="/static/collective/wallet.svg"
                    height={418}
                    width={344}
                    alt="Upstream DAO Wallet"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="h-full mx-8 flex flex-col items-end justify-center max-w-[450px] text-right max-w-xs:text:center">
                  <Typography className="text-[2.125rem] text-center sm:text-right leading-10">
                    {language.collectiveMarketing.whatIsCollective}
                  </Typography>

                  <div className="mt-8">
                    <Typography className="text-[1.5rem] font-nunito text-gray-800 text-center sm:text-right leading-7">
                      {language.collectiveMarketing.whatIsCollectiveeInfo}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-20">
              <div className="flex flex-col-reverse sm:flex-row">
                <div className="flex-1">
                  <div className="flex flex-col mx-8 justify-end max-w-[450px] mt-8 sm:mt-0 h-full">
                    <Typography className="text-[2.125rem] text-center sm:text-left leading-10">
                      {language.collectiveMarketing.howToUseCollective}
                    </Typography>

                    <div className="my-8">
                      <Typography className="text-[1.5rem] font-nunito text-center sm:text-left leading-7">
                        {language.collectiveMarketing.howToUseCollectiveInfo}
                      </Typography>
                    </div>

                    <div className="mt-8 mx-auto sm:mx-0">
                      <a
                        href="https://help.upstreamapp.com/en/collections/3222695-upstream-collective-faqs"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button
                          className={`px-[70px] py-[14px] text-[0.9375rem] rounded-[10px]`}
                          label={language.collectiveMarketing.learnMore}
                          onClick={() =>
                            window.analytics.track(
                              `${analyticsPreFix}: Learn More`
                            )
                          }
                          color="transparent"
                        />
                      </a>
                    </div>
                  </div>
                </div>
                <div className="flex-1 max-w-xs:hidden">
                  <div className="mt-10 mr-2 ml-4 flex justify-center">
                    <Image
                      src="/static/collective/vote.svg"
                      height={488}
                      width={304}
                      alt="Upstream Collectice Vote"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CenterContainer>

        <CenterContainer>
          <div className="flex items-center text-center flex-col px-4 mt-32 gap-8">
            <Typography className="text-[34px]">
              {language.collectiveMarketing.communityWalletCollectiveAction}
            </Typography>

            <div className="max-w-[850px]">
              <Typography fontWeight="light" className="font-nunito text-2xl">
                {language.collectiveMarketing.communitySelfGovern}
              </Typography>
            </div>
          </div>

          <div
            className={`max-w-full mt-16 mb-32 px-16 text-center ${pageWidth}`}
          >
            <div className="flex flex-col sm:flex-row gap-5">
              <div className="flex-1">
                <div className="flex flex-1 items-center flex-col mt-8">
                  <div className="h-[150px]">
                    <Image
                      src="/static/collective/together.svg"
                      width={95}
                      height={97}
                      alt="Upstream Collectice Together"
                    />
                  </div>

                  <Typography>
                    {language.collectiveMarketing.comeTogether}
                  </Typography>

                  <div className="mt-6">
                    <Typography fontWeight="light" className="font-nunito">
                      {language.collectiveMarketing.comeTogetherInfo}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-1 mt-8 items-center flex-col">
                  <div className="h-[150px]">
                    <Image
                      src="/static/collective/raise.svg"
                      width={104}
                      height={103}
                      alt="Upstream Collectice Raise"
                    />
                  </div>

                  <Typography>
                    {language.collectiveMarketing.raiseMoney}
                  </Typography>

                  <div className="mt-6">
                    <Typography fontWeight="light" className="font-nunito">
                      {language.collectiveMarketing.raiseMoneyInfo}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-1 items-center flex-col mt-8">
                  <div className="h-[150px]">
                    <Image
                      src="/static/collective/decision.svg"
                      width={105}
                      height={111}
                      alt="Upstream Collectice Decide"
                    />
                  </div>

                  <Typography>
                    {language.collectiveMarketing.makeDecisions}
                  </Typography>

                  <div className="mt-6">
                    <Typography fontWeight="light" className="font-nunito">
                      {language.collectiveMarketing.makeDecisionsInfo}
                    </Typography>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-1 items-center flex-col mt-8">
                  <div className="h-[150px]">
                    <Image
                      src="/static/collective/action.svg"
                      width={102}
                      height={123}
                      alt="Upstream Collectice Act"
                    />
                  </div>

                  <Typography>
                    {language.collectiveMarketing.takeAction}
                  </Typography>

                  <div className="mt-6">
                    <Typography fontWeight="light" className="font-nunito">
                      {language.collectiveMarketing.takeActionInfo}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="gradient-box flex flex-col items-center justify-center bg-blue py-16 mb-20">
            <div className="mb-2">
              <Typography className="text-white text-5xl font-bold">
                Current Features
              </Typography>
            </div>
            <div className="px-10 mt-12">
              <div className="grid grid-cols-1 grid-rows-3 gap-6 sm:grid-cols-3 sm:grid-rows-1">
                <Card>
                  <>
                    <Typography className="text-xl font-bold">
                      Shared Wallet
                    </Typography>
                    <Typography fontWeight="light">
                      Bring together money, NFTs, and other web3 tokens in a
                      shared wallet. Contribute to the DAO and vote on proposals
                      to decide what actions to take as a community.
                    </Typography>
                  </>
                </Card>

                <Card>
                  <>
                    <Typography className="text-xl font-bold">
                      Proposals &amp; Voting
                    </Typography>
                    <Typography fontWeight="light">
                      Every DAO member can create, discuss, and vote on
                      proposals. Proposals need a majority vote to pass and
                      quorum to be reached for the decision to be executed.
                    </Typography>
                  </>
                </Card>

                <Card>
                  <>
                    <Typography className="text-xl font-bold">
                      Group Chat
                    </Typography>
                    <Typography fontWeight="light">
                      Talk about proposals, plans, and absolutely anything in
                      the DAO-wide chat. No need to move between Discord or
                      Slack.
                    </Typography>
                  </>
                </Card>
              </div>

              <div className="grid grid-cols-1 grid-rows-3 gap-6 sm:grid-cols-3 sm:grid-rows-1 mt-6">
                <Card>
                  <>
                    <Typography className="text-xl font-bold">
                      Dynamic Delegation
                    </Typography>
                    <Typography fontWeight="light">
                      If you don’t want to vote on every proposal, you are able
                      to delegate your votes to another person in the DAO. You
                      may delegate all of your voting power for all proposals to
                      one person or to different people for different proposals.
                    </Typography>
                  </>
                </Card>

                <Card>
                  <>
                    <Typography className="text-xl font-bold">
                      Member Directory
                    </Typography>
                    <Typography fontWeight="light">
                      See all of the members of your DAO in one central
                      location.
                    </Typography>
                  </>
                </Card>
                <Card>
                  <>
                    <Typography className="text-xl font-bold">
                      Virtual Events
                    </Typography>
                    <Typography fontWeight="light">
                      Host DAO-only events for your community members, hosted
                      online through Upstream’s video streaming service or IRL
                      using Upstream’s in-person event tool.
                    </Typography>
                  </>
                </Card>
              </div>
            </div>
          </div>

          <div className="max-w-[1440px] mb-24">
            <div className="flex flex-col md:flex-row">
              <div className="p-4 w-full md:w-1/2">
                <div className="bg-[#f0f6ff] p-6 sm:p-12 h-full">
                  <CenterContainer className="mb-8">
                    <Typography className="text-2xl">
                      Learn More About DAOs
                    </Typography>
                  </CenterContainer>
                  {LearnMoreAboutDaosData.map(dataProps => (
                    <MarketetingContentSection
                      key={dataProps.trackingString}
                      {...dataProps}
                    />
                  ))}
                </div>
              </div>

              <div className="p-4 w-full md:w-1/2">
                <div className="bg-[#f0f6ff] p-6 sm:p-12 h-full">
                  <CenterContainer className="mb-8">
                    <Typography className="text-2xl">
                      Learn More About NFTs
                    </Typography>
                  </CenterContainer>
                  {LearnMoreAboutNftsData.map(dataProps => (
                    <MarketetingContentSection
                      key={dataProps.trackingString}
                      {...dataProps}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CenterContainer>

        <div className="bg-[#F0F6FF] mt-28 p-16">
          <CenterContainer>
            <CenterContainer>
              <Typography
                fontWeight="light"
                size="lg"
                color="lightGray"
                className={`text-center`}
              >
                What People are Saying about Upstream DAOs
              </Typography>
            </CenterContainer>
            <div className="flex flex-col sm:flex-row mt-4 w-full items-center justify-center content-center">
              <div className="flex-1 max-w-[530px] p-0 pt-10 sm:p-10 hover:bg-white">
                <a
                  href="https://cheddar.com/media/upstream-collective-looks-to-make-creating-a-dao-more-accessible"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    window.analytics.track(`${analyticsPreFix}: Cheddar Quote`)
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    className={`text-[22px] font-nunito`}
                  >
                    {
                      '“Social platform Upstream is building a DAO-it-yourself kit”'
                    }
                  </Typography>
                  <div className="mt-8 flex justify-end">
                    <Image
                      src="/static/collective/cheddar.png"
                      width={291}
                      height={34}
                      alt="Upstream DAO Cheddar Review"
                    />
                  </div>
                </a>
              </div>
              <div className="flex-1 max-w-[530px] p-0 pt-10 sm:p-10 hover:bg-white">
                <a
                  href="https://www.theblockcrypto.com/post/124685/social-platform-upstream-is-building-a-dao-it-yourself-kit"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    window.analytics.track(
                      `${analyticsPreFix}: The Block Quote`
                    )
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    className={`text-[22px] font-nunito`}
                  >
                    {
                      '"\'Upstream DAO\' Looks to Make Creating a DAO More Accessible"'
                    }
                  </Typography>
                  <div className="mt-8 flex justify-end">
                    <Image
                      src="/static/collective/theblock.png"
                      width={214}
                      height={42}
                      alt="Upstream DAO The Block Review"
                    />
                  </div>
                </a>
              </div>
            </div>
          </CenterContainer>
        </div>
      </div>

      <Footer />
    </>
  )
}

export default CollectivesPage
